import { FC, useEffect, useState } from "react";
import { Tendering, TenderingPart } from "../../../models/tendering";
import { ActiveFilters, Filter } from "../../../models/filters";
import { Button, Dialog, DialogActions, DialogContent, DialogSurface, DialogTitle, Option, Dropdown, Field, Input, Popover, PopoverSurface, PopoverTrigger, Textarea, DialogBody } from "@fluentui/react-components";
import { PlacementDoc } from "../../../models/placements";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { useAppContext } from "../../../AppContext";
import FiltersComponent from "../FiltersComponent";
import { set } from "react-hook-form";
import { createPlacementDocument } from "../../../services/placementService";

import { useNavigate } from "react-router-dom";
import { UserGroup } from "../../../models/userGroup";
import { DismissRegular } from "@fluentui/react-icons";

interface Props {
  onClose: () => void,
  tenderingDocument: Tendering,
  open: boolean,
  }

const CreatePlacement: FC<Props> = ( props: Props ) => {
  const [tenderingPart, setTenderingPart] = useState<TenderingPart | null>(null);
  const [baseFilters, setBaseFilters] = useState<Filter[]>([]);
  const [activeBaseFilters, setActiveBaseFilters] = useState<Filter[]>([]);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({baseFilters: [], filters: [] });
  const [placement, setPlacement] = useState<PlacementDoc>();
  const [isCreateButtonEnabled, setIsCreateButtonEnabled] = useState(false);
  const [namePopoverOpen, setNamePopoverOpen] = useState(false);
  const [behovPopoverOpen, setBehovPopoverOpen] = useState(false);
  const [bakgrundPopoverOpen, setBakgrundPopoverOpen] = useState(false);
  const [userGroups, setUserGroups] = useState<any[]>([]);

  const {user} = useAppContext();

  const navigate = useNavigate();

  useEffect(() => {
    if(props.open && props.tenderingDocument){
      if(props.tenderingDocument.tenderingParts.length == 1 && props.open){
        setTenderingPart(props.tenderingDocument.tenderingParts[0]);
      }
      else{
        setTenderingPart(null);
      }
      
      if(user){
        let userUserGroups = (user.userGroupAssignments ?? [])
          .map((ug: any) => ug.userGroup)
          .filter((ug: any) => ug?.moduleID == props.tenderingDocument.moduleID)
          .filter((ug: any) => ug.userGroupAssignments?.some((member: any) =>
            member.user?.userID === user.user.userID && member.roles?.some((r: any) => r.roleName === "Kommunanvändare")
          ));
        setUserGroups(userUserGroups);
        setBaseFilters(props.tenderingDocument.properties.baseFilters ?? []);
        setPlacement(createBasePlacement(userUserGroups));
      }
    }
  }, [props.open, props.tenderingDocument]);

  useEffect(() => {
    if (placement) {
      const isValid = tenderingPart !== null
        && placement.plannedDate !== '' 
        && placement.name !== '';
      
      setIsCreateButtonEnabled(isValid);
    }
  }, [placement, tenderingPart]);

  const createBasePlacement = (userGroups: any[]): PlacementDoc => {
    const singlePart = props.tenderingDocument.tenderingParts.length === 1
      ? props.tenderingDocument.tenderingParts[0]
      : undefined;
    const initialActiveFilters: ActiveFilters = {
      ...activeFilters,
      ...(singlePart ? { tenderingPart: singlePart } : {})
    };
    return {
      name: `${new Date().toLocaleString("sv-SE")} - ${props.tenderingDocument.tenderingName}`,
      id: Math.round((Math.random() * 1000000)),
      organizationID: user.organization.organizationID,
      moduleID: props.tenderingDocument.moduleID,
      organizationName: user.organization.organizationName,
      tenderingID: props.tenderingDocument.moduleID,
      tenderingName: props.tenderingDocument.tenderingName,
      baseFilters: props.tenderingDocument.baseFilters,
      filters: props.tenderingDocument.filters ?? [],
      descriptionText: '',
      backgroundText: '',
      plannedDate: '',
      answerDate: '',
      activeFilters: initialActiveFilters,
      tenderingPart: props.tenderingDocument.tenderingParts[0],
      tenders: [],
      requestOpen: false,
      status: 'search',
      user: user.user,
      userGroupID: userGroups[0]?.userGroupID ?? 0,
      userGroupName: userGroups[0]?.userGroupName ?? '',
      logs: {
        created: new Date().toISOString(),
        createdByUser: user.user.email,
        filterChanges: [],
        tenderChanges: []
      }
    }
  }

  const onTenderingPartChange = (id: number) => {
    const tp = props.tenderingDocument.tenderingParts.find((tenderingPart: TenderingPart) => tenderingPart.tenderingPartID == id);
    setTenderingPart(tp ?? null);
    const filteredActiveBaseFilters: Filter[] = activeBaseFilters.filter(filter => checkFilterTenderingPart(filter));
    setActiveBaseFilters(filteredActiveBaseFilters);
    if (tp) {
      setPlacement(prev => prev ? { 
        ...prev,
        activeFilters: {
          ...prev.activeFilters,
          tenderingPart: tp
        },
        tenderingPart: tp
      } : undefined);
    }
  }

  const onUserGroupChange = (id: number) => {
    const selectedUserGroup = userGroups.find(ug => ug.userGroupID === id);
    setPlacement(prev => prev ? { 
      ...prev, 
      userGroupID: id,
      userGroupName: selectedUserGroup?.userGroupName ?? ''
    } : undefined);
  }

  const checkFilterTenderingPart = (filter: Filter): boolean => {
    if(!filter.tenderingPart || (filter.tenderingPart && filter.tenderingPart.length < 1)){
      return true;
    }
    else if(filter.tenderingPart.find(tp => tp.tenderingPartID == tenderingPart?.tenderingPartID)){
      return true;
    }

    return false;
  }

  const onFieldChange = (property: string, value: any) => {
    setPlacement(prev => prev ? { 
      ...prev, 
      [property]: value
    } : undefined);
  }
  

  const handleFilterChange = (filter: Filter): void => {
    const current: ActiveFilters = placement?.activeFilters ?? activeFilters;
    const filteredBaseFilters = current.baseFilters.filter(f => f.filterID !== filter.filterID);
    const updatedBaseFilters = filter.value != null ? [...filteredBaseFilters, filter] : filteredBaseFilters;
    const updatedActiveFilters: ActiveFilters = { ...current, baseFilters: updatedBaseFilters };
    setActiveFilters(updatedActiveFilters);
    setPlacement(prev => prev ? {
      ...prev,
      activeFilters: updatedActiveFilters
    } : undefined);
  }

  const createPlacement = async () => {
    setIsCreateButtonEnabled(false);
    await createPlacementDocument(placement!).then((res) => {
      navigate(`/forfragningar/${res}?ny=true`);
    }).catch((error) => {
      console.error("Error creating placement:", error);
      setIsCreateButtonEnabled(true);
    });
  }

  if (!props.tenderingDocument) {
    return null;
  }

  return (
    <Dialog 
      open={props.open} 
      onOpenChange={(_event, data) => !data.open && props.onClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
          action={
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              shape="circular"
              onClick={props.onClose}
              style={{ position: 'absolute', top: '20px', right: '20px' }}
            />
          }
          >Skapa förfrågan: {props.tenderingDocument.tenderingName}</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-2 mt-4">
              {/* Your form elements go here */}
              <Popover open={namePopoverOpen} size="small" appearance="inverted" positioning={{ position: 'after', align: 'start' }} withArrow>
                <PopoverTrigger disableButtonEnhancement>
                  <div>
                    <Field label="Namn på förfrågan" required>
                        <Input 
                          value={placement?.name ?? ''}
                          onChange={e => onFieldChange('name', e.target.value)}
                          onFocus={() => setNamePopoverOpen(true)}
                          onBlur={() => setNamePopoverOpen(false)}
                          onKeyDown={e => e.stopPropagation()}
                        />
                    </Field>
                  </div>
                </PopoverTrigger>
                <PopoverSurface>
                  <div>Det är fritt att själv definiera <br /> eget namn för förfrågan. <br /> Inkludera inte <br /> personuppgifter.</div>
                </PopoverSurface>
              </Popover>
              <Popover open={behovPopoverOpen} size="small" appearance="inverted" positioning={{ position: 'after', align: 'start' }} withArrow>
                <PopoverTrigger disableButtonEnhancement>
                  <div>
                    <Field label="Behov">
                      <Textarea 
                        onChange={e => onFieldChange('descriptionText', e.target.value)} 
                        resize="vertical"
                        onFocus={() => setBehovPopoverOpen(true)}
                        onBlur={() => setBehovPopoverOpen(false)}
                        onKeyDown={e => e.stopPropagation()}
                      />
                    </Field>
                  </div>
                </PopoverTrigger>
                <PopoverSurface>
                  Inkludera inte <br /> personuppgifter i<br /> beskrivningen.
                </PopoverSurface>
              </Popover>
              <Popover open={bakgrundPopoverOpen} size="small" appearance="inverted" positioning={{ position: 'after', align: 'start' }} withArrow>
                <PopoverTrigger disableButtonEnhancement>
                  <div>
                    <Field label="Bakgrund">
                      <Textarea 
                        onChange={e => onFieldChange('backgroundText', e.target.value)} 
                        resize="vertical"
                        onFocus={() => setBakgrundPopoverOpen(true)}
                        onBlur={() => setBakgrundPopoverOpen(false)}
                        onKeyDown={e => e.stopPropagation()}
                      />
                    </Field>
                  </div>
                </PopoverTrigger>
                <PopoverSurface>
                  Inkludera inte <br /> personuppgifter i<br /> beskrivningen.
                </PopoverSurface>
              </Popover>
              <Field label="Planerad start för uppdrag" required>
                <DatePicker
                  value={placement?.plannedDate ? new Date(placement.plannedDate) : null}
                  formatDate={(date?: Date) => { if(!date) return ''; return date.toLocaleDateString('sv-SE') }}
                  firstDayOfWeek={1}
                  onSelectDate={(date: Date | null | undefined) => {
                    if (date) {
                      onFieldChange('plannedDate', date.toISOString());
                    } else {
                      onFieldChange('plannedDate', '');
                    }
                  }}
                  showWeekNumbers={true}
                  firstWeekOfYear={1}
                  showMonthPickerAsOverlay={true} />
              </Field>
              {userGroups.length > 1 && <Field label="Välj grupp">
                <Dropdown
                  placeholder="Välj grupp"
                  value={placement?.userGroupID ? userGroups.find(ug => ug.userGroupID === placement.userGroupID)?.userGroupName : undefined}
                  onOptionSelect={(_, data) => {
                    if (data.optionValue !== undefined) {
                      onUserGroupChange(+data.optionValue);
                    }
                  }}
                  >
                  {userGroups.map((ug: any) => (
                    <Option key={ug.userGroupID} value={ug.userGroupID.toString()}>
                      {ug.userGroupName}
                    </Option>
                  ))}
                </Dropdown>
              </Field>}
              {props.tenderingDocument.tenderingParts.length > 1 && <Field label="Välj delområde" required>
                <Dropdown
                  placeholder="Välj delområde"
                  onOptionSelect={(_, data) => {
                    if (data.optionValue !== undefined) {
                      onTenderingPartChange(+data.optionValue);
                    }
                  }}
                  >
                  {props.tenderingDocument.tenderingParts.map((tp: TenderingPart) => (
                    tp.evaluated && <Option key={tp.tenderingPartID} value={tp.tenderingPartID.toString()}>
                      {tp.tenderingPart}
                    </Option>
                  ))}
                </Dropdown>
              </Field>}
              {tenderingPart && (
                <>
                  {baseFilters.map((filter: Filter) => (
                    <FiltersComponent 
                      filter={filter}
                      activeFilters={activeFilters}
                      key={filter.filterID}
                      onFilterChange={handleFilterChange}
                    />
                  ))}
                </>
              )}
            </div>
            
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose}>Avbryt</Button>
            <Button appearance="primary" disabled={!isCreateButtonEnabled} onClick={createPlacement}>Nästa</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default CreatePlacement;